import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  OnDestroy,
  OnInit,
  Output,
  TemplateRef,
  ViewChild,
  inject,
} from '@angular/core';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { BehaviorSubject, Subject, finalize, map, takeUntil } from 'rxjs';
import { DRAWER_IDS } from '../../../../../../core/constants/drawer-ids.constant';
import { DrawerService } from '../../../../../../core/services/drawer.service';
import { BehaviorInfoCardComponent } from '../../../../../../components/behavior-info-card/behavior-info-card.component';
import { StudentInfoCardComponent } from '../../../../../../components/student-info-card/student-info-card.component';
import { ManageStudentDrawerParams } from '../../../../insights.interface';
import { VideoModalComponent } from '../../../../../../components/video-modal/video-modal.component';
import { ProactiveStrategyComponent } from '../proactive-strategy/proactive-strategy.component';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzCheckboxModule } from 'ng-zorro-antd/checkbox';
import { NzFormModule } from 'ng-zorro-antd/form';
import { NzRadioModule } from 'ng-zorro-antd/radio';
import { NzTableModule, NzTableQueryParams } from 'ng-zorro-antd/table';
import { SvgIconComponent } from 'angular-svg-icon';
import { SearchSelectComponent } from '../../../../../../components/search-select/search-select.component';
import { TableQueryService } from '../../../../../../core/services/table-query.service';
import { TableQueryParams } from '../../../../../../core/models/table.models';
import { LoaderService } from '../../../../../../core/services/loader.service';
import { CommonService } from '../../../../../../shared/services/common.service';
import { ErrorResponse } from '../../../../../../core/models/error.models';
import { ManageBehaviorStrategiesService } from '../manage-behavior-strategies.service';
import { StrategyItem } from '../strategy.interface';
import { StrategySelectionService } from '../strategy-selection.service';
import { DrawerHeaderComponent } from '../../../../../../components/drawer-header/drawer-header.component';

export interface StrategySelectionDrawerParams {
  caseParams: ManageStudentDrawerParams;
  options: number[];
  defaultOptions: { id: number; value: string }[];
}

export interface StrategyFlowAction {
  type: 'open-create-own' | 'add-selected';
  source: 'Guide me' | 'Create my own';
  ids?: number[];
  strategy?: StrategyItem | null;
  mode?: 'edit' | 'create' | 'guide';
  from?: 'Guide me' | 'Create my own';
  strategyTypeId?: number;
  strategyType?: string;
}

export type StrategySelectionDrawerCloseResult =
  | ({ type: 'open-create-own' } & StrategyFlowAction)
  | ({ type: 'add-selected' } & StrategyFlowAction)
  | undefined;

@Component({
  selector: 'app-strategy-selection-drawer',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    StudentInfoCardComponent,
    BehaviorInfoCardComponent,
    NzCheckboxModule,
    NzFormModule,
    NzRadioModule,
    NzTableModule,
    SvgIconComponent,
    SearchSelectComponent,
    VideoModalComponent,
    NzButtonModule,
    DrawerHeaderComponent,
  ],
  templateUrl: './strategy-selection-drawer.component.html',
  styleUrls: ['./strategy-selection-drawer.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class StrategySelectionDrawerComponent implements OnInit, OnDestroy {
  @ViewChild('drawerBehaviorStrategies')
  drawerBehaviorStrategies!: TemplateRef<void>;

  @Output() flowAction = new EventEmitter<StrategyFlowAction>();

  private readonly drawerService = inject(DrawerService);
  private readonly strategyService = inject(ManageBehaviorStrategiesService);
  private readonly selectionService = inject(StrategySelectionService);
  private readonly tableQueryService = inject(TableQueryService);
  private readonly loaderService = inject(LoaderService);
  private readonly commonService = inject(CommonService);
  private readonly destroy$ = new Subject<void>();

  caseParams!: ManageStudentDrawerParams;
  options: number[] = [];
  defaultOptions: { id: number; value: string }[] = [];
  readonly typeControl = new FormControl<number | null>(null);
  currentStep: 'selection' | 'guide' = 'selection';
  selectedOption: 'Guide me' | 'Create my own' = 'Guide me';
  selectedId: number | null = null;
  selectedIds: number[] = [];

  isVideoVisible = false;
  videoUrl = '';

  private strategiesData: StrategyItem[] = [];
  private publishStrategiesData: StrategyItem[] = [];

  private readonly guideDefaultParams: TableQueryParams = {
    pageIndex: 1,
    pageSize: 10,
    sortField: undefined,
    sortOrder: undefined,
  };

  private readonly publishDefaultParams: TableQueryParams = {
    pageIndex: 1,
    pageSize: 10,
    sortField: undefined,
    sortOrder: undefined,
  };

  readonly loading$ = this.loaderService.loading$;

  readonly strategies$ = this.strategyService.strategiesSubject.pipe(
    map(response => {
      const items = response?.result?.items ?? [];
      this.strategiesData = items;
      return items;
    })
  );

  readonly stratCount$ = this.strategyService.strategiesSubject.pipe(
    map(response => response?.result?.totalRows ?? 0)
  );

  readonly publishStrategies$ =
    this.strategyService.publishStrategiesSubject.pipe(
      map(response => {
        const items = response?.result?.items ?? [];
        this.publishStrategiesData = items;
        return items;
      })
    );

  readonly pubCount$ = this.strategyService.publishStrategiesSubject.pipe(
    map(response => response?.result?.totalRows ?? 0)
  );

  readonly guideParams$ = new BehaviorSubject<TableQueryParams>(
    this.guideDefaultParams
  );

  ngOnInit(): void {
    this.selectionService.resetSelection$
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.handleResetSelection();
      });

    this.drawerService
      .getParams<StrategySelectionDrawerParams>(
        DRAWER_IDS.MANAGE_BEHAVIOR_STRATEGY_SELECTION
      )
      .pipe(takeUntil(this.destroy$))
      .subscribe(params => {
        if (!params) {
          return;
        }

        this.caseParams = params.caseParams;
        this.options = params.options;
        this.defaultOptions = params.defaultOptions;
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.guideParams$.complete();
  }

  get strategyType(): string {
    const typeId = this.typeControl?.value;
    if (typeId === null || typeId === undefined) {
      return '';
    }

    const option = this.defaultOptions.find(item => item.id === typeId);
    return option?.value || '';
  }

  get isTypeSelected(): boolean {
    return !!this.typeControl?.value;
  }

  get isGuideStep(): boolean {
    return this.currentStep === 'guide';
  }

  get isSelectionStep(): boolean {
    return this.currentStep === 'selection';
  }

  getStrategyTypeText(): string {
    const selectedType = this.typeControl.value;
    const strategyTexts: Record<number, string> = {
      71: 'Replacement Behavior strategies',
      72: 'Proactive strategies',
      73: 'Reactive strategies',
    };

    return selectedType ? strategyTexts[selectedType] : 'strategies';
  }

  onOptionChange(value: 'Guide me' | 'Create my own'): void {
    this.selectedOption = value;
    this.selectedId = null;
    this.selectedIds = [];
  }

  onCheckboxChange(id: number, checked: boolean): void {
    if (checked) {
      if (!this.selectedIds.includes(id)) {
        this.selectedIds.push(id);
      }
      return;
    }

    this.selectedIds = this.selectedIds.filter(item => item !== id);
  }

  onGuideQueryParamsChange(params: NzTableQueryParams): void {
    const queryParams = this.tableQueryService.buildQueryParams(
      params,
      this.guideDefaultParams
    );

    this.loaderService.show();

    this.strategyService
      .getStrategyList({
        caseBehaviorId: this.caseParams?.behavior?.id,
        strategyType: this.typeControl.value ?? undefined,
        ...queryParams,
      })
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => this.loaderService.hide())
      )
      .subscribe({
        next: () => {
          this.guideDefaultParams.pageIndex = params.pageIndex;
          this.guideDefaultParams.pageSize = params.pageSize;
          this.guideParams$.next({ ...this.guideDefaultParams });
        },
        error: ({ errors, message }: ErrorResponse) => {
          const errorMessages = errors.map(
            err => err.description || err.detailedMessage
          );
          this.commonService.notify('error', errorMessages, message);
        },
      });
  }

  onPublishStrategyQueryParamsChange(params: NzTableQueryParams): void {
    if (this.selectedOption !== 'Create my own') {
      return;
    }

    const queryParams = this.tableQueryService.buildQueryParams(
      params,
      this.publishDefaultParams
    );

    this.loaderService.show();

    this.strategyService
      .getPublishStrategyList({
        organizationId: this.caseParams.orgId ?? 0,
        caseBehaviorId: this.caseParams?.behavior?.id ?? 0,
        ...queryParams,
      })
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => this.loaderService.hide())
      )
      .subscribe({
        next: () => {
          this.publishDefaultParams.pageIndex = params.pageIndex;
          this.publishDefaultParams.pageSize = params.pageSize;
        },
        error: ({ errors, message }: ErrorResponse) => {
          const errorMessages = errors.map(
            err => err.description || err.detailedMessage
          );
          this.commonService.notify('error', errorMessages, message);
        },
      });
  }

  onPrevious(): void {
    if (this.isGuideStep) {
      this.currentStep = 'selection';
      this.selectedId = null;
      this.selectedIds = [];
      return;
    }

    this.onCancel();
  }

  onNext(): void {
    if (this.isSelectionStep && this.selectedOption === 'Guide me') {
      // Validate strategy type is selected
      if (!this.isTypeSelected) {
        this.commonService.notify(
          'error',
          ['Please select a Behavior Strategy Type before proceeding'],
          'Validation Error'
        );
        return;
      }
      this.currentStep = 'guide';
      this.selectedId = null;
      this.selectedIds = [];
      return;
    }

    if (this.isGuideStep) {
      this.handleGuideNextAction();
      return;
    }

    this.handleCreateOwnOption();
  }

  onCancel(): void {
    this.closeSelectionDrawer();
  }

  openVideoUrl(url: string): void {
    this.videoUrl = url;
    this.isVideoVisible = true;
  }

  openProactiveStrategyDrawer(data: StrategyItem): void {
    this.drawerService.openDrawer(
      ProactiveStrategyComponent,
      this.drawerBehaviorStrategies as unknown as TemplateRef<object>,
      1170,
      true,
      undefined,
      {
        data,
        mode: this.currentStep === 'guide' ? 'edit' : 'create',
        behaviorId: this.caseParams?.behavior?.id,
        caseParams: this.caseParams,
      },
      DRAWER_IDS.MANAGE_BEHAVIOR_STRATEGIES_DETAIL
    );
  }
  closeManageBehaviorStrategiesDrawer() {
    const drawer = this.drawerService.getDrawerRef(
      DRAWER_IDS.MANAGE_BEHAVIOR_STRATEGIES_DETAIL
    );
    drawer?.close({
      navigationHandled: true,
      isContinueProcessing: this.caseParams.isContinueProcessing ?? false,
    });
  }

  private handleGuideNextAction(): void {
    const isMultiSelect = !this.options.includes(5);
    const selectedGuideIds = [...new Set(this.selectedIds)];
    const hasSelection = isMultiSelect
      ? selectedGuideIds.length > 0
      : this.selectedId !== null;

    if (!hasSelection) {
      return;
    }

    if (isMultiSelect) {
      const action: StrategyFlowAction = {
        type: 'add-selected',
        source: 'Guide me',
        ids: selectedGuideIds,
        strategyTypeId: this.typeControl.value ?? undefined,
      };
      this.flowAction.emit(action);
      this.selectionService.emitFlowAction(action);
      return;
    }

    if (this.selectedId) {
      const strategy = this.strategiesData.find(
        item => item.id === this.selectedId
      );

      const action: StrategyFlowAction = {
        type: 'open-create-own',
        source: 'Guide me',
        mode: 'guide',
        from: 'Guide me',
        strategy: strategy ?? null,
        strategyTypeId: this.typeControl.value ?? undefined,
        strategyType: this.strategyType,
      };
      this.flowAction.emit(action);
      this.selectionService.emitFlowAction(action);
    }
  }

  private handleCreateOwnOption(): void {
    const hasSelection = this.selectedId || this.selectedIds.length;
    const isMultiSelect = !this.options.includes(5);
    const shouldCreateNew =
      (this.options.includes(5) && !this.selectedId) ||
      (!this.options.includes(5) && !this.selectedIds.length);

    if (shouldCreateNew) {
      const action: StrategyFlowAction = {
        type: 'open-create-own',
        source: 'Create my own',
        mode: 'create',
        strategy: null,
        strategyTypeId: this.typeControl.value ?? undefined,
        strategyType: this.strategyType,
      };
      this.flowAction.emit(action);
      this.selectionService.emitFlowAction(action);
      return;
    }

    if (hasSelection && isMultiSelect) {
      const action: StrategyFlowAction = {
        type: 'add-selected',
        source: 'Create my own',
        ids: [...this.selectedIds],
        strategyTypeId: this.typeControl.value ?? undefined,
      };
      this.flowAction.emit(action);
      this.selectionService.emitFlowAction(action);
      return;
    }

    if (this.selectedId) {
      const strategy = this.publishStrategiesData.find(
        item => item.id === this.selectedId
      );

      const action: StrategyFlowAction = {
        type: 'open-create-own',
        source: 'Create my own',
        mode: 'edit',
        from: 'Create my own',
        strategy: strategy ?? null,
        strategyTypeId: this.typeControl.value ?? undefined,
        strategyType: this.strategyType,
      };
      this.flowAction.emit(action);
      this.selectionService.emitFlowAction(action);
    }
  }

  private closeSelectionDrawer(
    result?: StrategySelectionDrawerCloseResult
  ): void {
    const drawer = this.drawerService.getDrawerRef(
      DRAWER_IDS.MANAGE_BEHAVIOR_STRATEGY_SELECTION
    );
    drawer?.close(result);
  }

  private handleResetSelection(): void {
    this.selectedId = null;
    this.selectedIds = [];

    if (this.isGuideStep) {
      this.refreshGuideStrategies();
      return;
    }

    if (this.selectedOption === 'Create my own') {
      this.refreshPublishStrategies();
    }
  }

  private refreshGuideStrategies(): void {
    this.loaderService.show();

    this.strategyService
      .getStrategyList({
        caseBehaviorId: this.caseParams?.behavior?.id,
        strategyType: this.typeControl.value ?? undefined,
        ...this.guideDefaultParams,
      })
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => this.loaderService.hide())
      )
      .subscribe({
        error: ({ errors, message }: ErrorResponse) => {
          const errorMessages = errors.map(
            err => err.description || err.detailedMessage
          );
          this.commonService.notify('error', errorMessages, message);
        },
      });
  }

  private refreshPublishStrategies(): void {
    this.loaderService.show();

    this.strategyService
      .getPublishStrategyList({
        organizationId: this.caseParams.orgId ?? 0,
        caseBehaviorId: this.caseParams?.behavior?.id ?? 0,
        ...this.publishDefaultParams,
      })
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => this.loaderService.hide())
      )
      .subscribe({
        error: ({ errors, message }: ErrorResponse) => {
          const errorMessages = errors.map(
            err => err.description || err.detailedMessage
          );
          this.commonService.notify('error', errorMessages, message);
        },
      });
  }
}
